import { useState } from "react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Card, Field, ConfirmDialog, SectionTitle } from "@/components/lf";
import { useStore } from "@/lib/hooks";
import { read, write, resetDemoData, StorageError } from "@/lib/storage";

const Disclosure = ({ title, children, testid }) => (
  <div className="border-t border-[#29292D] py-5" data-testid={testid}>
    <h3 className="font-serif text-xl mb-1.5">{title}</h3>
    <div className="text-sm text-[#929197] leading-relaxed">{children}</div>
  </div>
);

export default function Pengaturan() {
  const profile = useStore("profile");
  const settings = useStore("settings");
  const [p, setP] = useState(profile.displayName);
  const [s, setS] = useState(settings);
  const [reset, setReset] = useState(false);

  const save = (e) => {
    e.preventDefault();
    if (!p.trim()) return toast.error("Nama tampilan tidak boleh kosong.");
    try {
      write("profile", { ...read("profile"), displayName: p.trim() });
      write("settings", {
        ...read("settings"), ...s,
        annualGoalBooks: Math.max(1, Number(s.annualGoalBooks) || 1),
        dailyMinutes: Math.max(5, Number(s.dailyMinutes) || 5),
      });
      toast.success("Pengaturan disimpan.");
    } catch (err) { toast.error(err instanceof StorageError ? err.message : "Gagal menyimpan."); }
  };

  const doReset = () => {
    resetDemoData();
    setReset(false);
    setP(read("profile").displayName);
    setS(read("settings"));
    toast.success("Data demo direset ke kondisi awal.");
  };

  return (
    <div>
      <PageHeader testid="page-title-pengaturan" overline="Akun" title="Pengaturan" subtitle="Atur profil dan preferensi membaca. Seluruh informasi tentang prototipe ini ada di halaman ini." />
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-10">
        <form onSubmit={save} className="xl:col-span-3 space-y-8" data-testid="settings-form">
          <Card className="space-y-5">
            <SectionTitle>Profil</SectionTitle>
            <Field label="Nama tampilan"><input data-testid="settings-name-input" className="lf-field" value={p} onChange={(e) => setP(e.target.value)} maxLength={60} /></Field>
            <Field label="Email profil demo" hint="Email tetap untuk akun demo; tidak dapat diubah."><input data-testid="settings-email-input" className="lf-field opacity-60" value={profile.email} readOnly disabled /></Field>
          </Card>
          <Card className="space-y-5">
            <SectionTitle>Preferensi membaca</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Tingkat pengetahuan default"><select data-testid="settings-level-select" className="lf-field" value={s.preferredLevel} onChange={(e) => setS({ ...s, preferredLevel: e.target.value })}>{["Pemula", "Menengah", "Lanjutan"].map((x) => <option key={x}>{x}</option>)}</select></Field>
              <Field label="Intensitas default"><select data-testid="settings-intensity-select" className="lf-field" value={s.preferredIntensity} onChange={(e) => setS({ ...s, preferredIntensity: e.target.value })}>{["Ringan", "Sedang", "Mendalam"].map((x) => <option key={x}>{x}</option>)}</select></Field>
              <Field label="Target buku per tahun"><input data-testid="settings-goal-input" type="number" min="1" className="lf-field" value={s.annualGoalBooks} onChange={(e) => setS({ ...s, annualGoalBooks: e.target.value })} /></Field>
              <Field label="Target membaca harian (menit)"><input data-testid="settings-daily-input" type="number" min="5" className="lf-field" value={s.dailyMinutes} onChange={(e) => setS({ ...s, dailyMinutes: e.target.value })} /></Field>
            </div>
            <Field label="Tema" hint="Prototipe ini hanya menampilkan tema gelap; pilihan tersimpan untuk pengembangan berikutnya."><select data-testid="settings-theme-select" className="lf-field" value={s.theme} onChange={(e) => setS({ ...s, theme: e.target.value })}><option value="gelap">Gelap</option><option value="otomatis">Ikuti sistem</option></select></Field>
          </Card>
          <Card className="space-y-4">
            <SectionTitle>Notifikasi</SectionTitle>
            <div className="flex items-center justify-between gap-4"><div><div className="text-sm">Pengingat membaca harian</div><div className="text-xs text-[#929197]">Pengaturan tersimpan; prototipe tidak mengirim notifikasi nyata.</div></div><Switch data-testid="settings-notif-reminder" checked={s.notifReminder} onCheckedChange={(v) => setS({ ...s, notifReminder: v })} /></div>
            <div className="flex items-center justify-between gap-4"><div><div className="text-sm">Ringkasan mingguan</div><div className="text-xs text-[#929197]">Ringkasan progres tiap pekan (belum aktif).</div></div><Switch data-testid="settings-notif-weekly" checked={s.notifWeekly} onCheckedChange={(v) => setS({ ...s, notifWeekly: v })} /></div>
          </Card>
          <button data-testid="settings-save-button" className="lf-btn !px-8">Simpan pengaturan</button>

          <Card className="border-[#7a3434]">
            <h2 className="font-serif text-2xl mb-1">Reset data demo</h2>
            <p className="text-sm text-[#929197] mb-4">Menghapus semua catatan, progres, rekomendasi, analisis, keranjang, pesanan, dan langganan demo dari peramban ini. Anda tetap masuk.</p>
            <button type="button" data-testid="reset-demo-button" onClick={() => setReset(true)} className="lf-btn-ghost !text-[#d98a8a] !border-[#7a3434]">Reset data demo…</button>
          </Card>
        </form>

        <aside className="xl:col-span-2" data-testid="disclosures">
          <div className="lf-overline mb-2">Informasi & pengungkapan</div>
          <Disclosure title="Penyimpanan data (LocalStorage)" testid="disclosure-storage">Seluruh catatan, progres, dan riwayat disimpan di peramban ini saja — tidak ada basis data dan tidak tersinkron antar perangkat. Menghapus data situs atau memakai mode privat akan menghilangkan data.</Disclosure>
          <Disclosure title="Batasan autentikasi prototipe" testid="disclosure-auth">Login hanya akun demo tunggal dan berjalan sepenuhnya di sisi klien. Ini bukan keamanan tingkat produksi, tidak ada pendaftaran, dan kata sandi tidak disimpan di LocalStorage.</Disclosure>
          <Disclosure title="Komersial (demonstrasi)" testid="disclosure-commerce">Toko Buku, keranjang, checkout, dan langganan adalah demonstrasi. Harga hanya contoh dan tidak ada pembayaran nyata.</Disclosure>
        </aside>
      </div>
      <ConfirmDialog open={reset} onOpenChange={setReset} testid="reset-confirm" title="Reset semua data demo?" description="Tindakan ini tidak dapat dibatalkan. Semua data yang tersimpan di peramban ini akan dihapus." confirmLabel="Ya, reset" onConfirm={doReset} />
    </div>
  );
}
